import React from "react";
import { Users, ArrowRight, AlertCircle, Check } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { buildWhatsAppLink, FLEET_STATIC } from "../data/static";
import { formatPrice } from "../utils/currency";

export const CabServices = () => {
  const { t } = useI18n();
  const c = t.cabs;

  return (
    <section id="cabs" data-testid="cab-services-section" className="relative py-20 lg:py-28 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D]">{c.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
            {c.title}
            <br className="hidden sm:block" />
            <span className="italic text-slate-500">{c.titleItalic}</span>
          </h2>
          <p className="mt-4 text-base text-slate-600 max-w-xl">{c.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {c.fleet.map((v, idx) => {
            const stat = FLEET_STATIC[v.id];
            const link = buildWhatsAppLink(c.waMsg(v.name, v.subtitle));
            return (
              <article key={v.id} data-testid={`cab-card-${v.id}`}
                className="group relative bg-white rounded-3xl overflow-hidden border border-slate-200/80 hover:border-[#D1603D]/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${idx * 60}ms` }}>
                <div className="relative h-52 overflow-hidden bg-slate-100">
                  <img src={stat.image} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] font-semibold tracking-wider uppercase text-slate-900 flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> {stat.seats} {c.seater}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <h3 className="font-display text-xl font-semibold text-slate-900">{v.name}</h3>
                      <p className="text-sm text-slate-500">{v.subtitle}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-2xl font-semibold text-[#D1603D] leading-none">{formatPrice(stat.pricePerKm)}</div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{c.perKm}</div>
                    </div>
                  </div>

                  <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2">
                    {v.features.map((f) => (
                      <li key={f} className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`cab-book-${v.id}`}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-[#D1603D] transition-colors active:scale-95 group/btn">
                    {c.enquireNow}
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        <div data-testid="fleet-terms"
          className="mt-10 lg:mt-12 rounded-3xl border border-[#D1603D]/15 bg-gradient-to-br from-[#FADBD1]/40 via-[#FAFAF7] to-[#FDE68A]/25 p-6 sm:p-8 lg:p-10 grid lg:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D1603D] text-white flex items-center justify-center shrink-0 shadow-lg shadow-[#D1603D]/20">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D1603D]">{c.termsBadge}</p>
              <p className="font-display text-2xl font-semibold mt-1 text-slate-900">{c.termsTitle}</p>
              <p className="text-sm text-slate-600 mt-1">{c.termsSubtitle}</p>
            </div>
          </div>
          <ul className="lg:col-span-2 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {c.terms.map((term) => (
              <li key={term} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-[#D1603D] mt-0.5 shrink-0" />
                {term}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default CabServices;
